
import { apiRequest } from '../api/client';
import { GenericDatabasePayload } from '../common/common.types';
import { toast } from 'sonner';
import {
  BulkUpdateSourceFileFieldsBody,
  CreateSourceApiResponse,
  CreateSourceResponseItem,
  DqDataProfilingBody,
  DqStatRecord,
  DqStatsBody,
  GetNotRegisteredTablesParams,
  GetRegisteredDataSourcesParams,
  GetRegisteredDataSourcesResponse,
  NotRegisteredTableDatabase,
  NotRegisteredTablesResponse,
  RegisteredSourceFilesResponse,
  SourceFileRegisteredField,
  SourceFilesBody
} from './source.types';

export const sourceService = {
  getRegisteredDataSource: async (
    params?: GetRegisteredDataSourcesParams
  ): Promise<GetRegisteredDataSourcesResponse> => {
    const queryParams: Record<string, string | number> = {};

    if (typeof params?.limit === 'number') {
      queryParams.limit = params.limit;
    }

    if (typeof params?.page === 'number') {
      queryParams.page = params.page;
    }

    if (params?.allowed_instances?.trim()) {
      queryParams.allowed_instances = params.allowed_instances.trim();
    }

    if (params?.source_name?.trim()) {
      queryParams.source_name = params.source_name.trim();
    }

    return apiRequest<GetRegisteredDataSourcesResponse>(
      {
        method: 'GET',
        url: '/source-data-sources/registered',
        params: queryParams,
      },
      'Failed to fetch registered source data sources'
    );
  },

  createSource: async (params: GenericDatabasePayload): Promise<CreateSourceResponseItem> => {
    const response = await apiRequest<CreateSourceApiResponse>({
      method: 'POST',
      url: '/source-data-sources',
      data: params,
    }, 'Failed to create source');

    if (Array.isArray(response)) {
      return response[0] ?? {};
    }

    return response;
  },

  createSourceFileAndFields: async (params: SourceFilesBody): Promise<SourceFileRegisteredField> => {
    return apiRequest<SourceFileRegisteredField>({
      method: 'POST',
      url: '/source-files',
      data: params,
    }, 'Failed to create source file and fields');
  },

  /** POST `/dq-stats` — register DQ pipeline run for the given source files / sessions. */
  kickoffDqStats: async (body: DqStatsBody): Promise<void> => {
    await apiRequest<unknown>({
      method: 'POST',
      url: '/dq-stats',
      data: body,
    }, 'DQ stats request failed');
  },

  /** POST `/dq-stats/data-profiling` — trigger the Spark profiling job for one source file. */
  triggerDataProfiling: async (body: DqDataProfilingBody): Promise<void> => {
    await apiRequest<unknown>({
      method: 'POST',
      url: '/dq-stats/data-profiling',
      data: body,
    }, 'Data profiling request failed');
  },

  /** GET `/records/dq_stats?source_file_id=&session_id=` — per-field profiling metrics. */
  getDqStatsBySourceFile: async (
    sourceFileId: number,
    sessionId: string,
  ): Promise<DqStatRecord[]> => {
    if (!Number.isInteger(sourceFileId) || sourceFileId <= 0) {
      throw new Error('source_file_id must be a positive integer for DQ stats fetch');
    }
    const trimmedSessionId = sessionId.trim();
    if (!trimmedSessionId) {
      throw new Error('session_id is required for DQ stats fetch');
    }

    const data = await apiRequest<DqStatRecord[]>({
      method: 'GET',
      url: '/records/dq_stats',
      params: {
        source_file_id: sourceFileId,
        session_id: trimmedSessionId,
      },
    }, 'DQ stats fetch failed');
    return Array.isArray(data) ? data : [];
  },

  bulkUpdateSourceFileFields: async (params: BulkUpdateSourceFileFieldsBody): Promise<void> => {
    await apiRequest<unknown>({
      method: 'POST',
      url: '/source-file-fields/bulk-update',
      data: params,
    }, 'Bulk update source file fields failed');
  },

  listRegisteredSourceFiles: async (
    sourceDataSourceId: number,
    tableName?: string
  ): Promise<RegisteredSourceFilesResponse> => {
    const params: Record<string, string | number> = {
      source_data_source_id: sourceDataSourceId,
    };

    if (tableName?.trim()) {
      params.table_name = tableName.trim();
    }

    const request = apiRequest<RegisteredSourceFilesResponse>({
      method: 'GET',
      url: '/source-files/registered',
      params,
    }, 'Failed to list registered source files');

    return toast.promise(request, {
      loading: 'Loading reference tables...',
      success: 'Reference tables loaded',
      error: 'Failed to load reference tables',
    }).unwrap();
  },

  /**
   * Silent variant of `listRegisteredSourceFiles` — same API call, no
   * loading / success toasts. Use for background pre-checks (e.g. Explorer
   * dialog Save pre-flight, Join sheet auto-fetch) where the toast.promise
   * UX would be noise. The axios error interceptor still toasts hard failures.
   */
  listRegisteredSourceFilesSilent: async (
    sourceDataSourceId: number,
    tableName?: string
  ): Promise<RegisteredSourceFilesResponse> => {
    const params: Record<string, string | number> = {
      source_data_source_id: sourceDataSourceId,
    };

    if (tableName?.trim()) {
      params.table_name = tableName.trim();
    }

    return apiRequest<RegisteredSourceFilesResponse>({
      method: 'GET',
      url: '/source-files/registered',
      params,
    }, 'Failed to list registered source files');
  },

  /**
   * GET `/source-data-sources/not-registered-tables` — physical tables on the
   * source that have not been registered yet, plus a `databases` block carrying
   * the source's connection config (needed for `/connections/columns`).
   */
  getNotRegisteredTables: async (
    params: GetNotRegisteredTablesParams,
  ): Promise<NotRegisteredTablesResponse> => {
    const data = await apiRequest<unknown>({
      method: 'GET',
      url: '/source-data-sources/not-registered-tables',
      params: {
        source_data_source_id: params.source_data_source_id,
        type: params.type,
      },
    }, 'Failed to fetch available tables');

    const envelope = (data && typeof data === 'object' && !Array.isArray(data))
      ? data as { tables?: unknown; databases?: unknown }
      : null;

    const rawTables = Array.isArray(data)
      ? data
      : Array.isArray(envelope?.tables)
        ? envelope.tables
        : [];

    const tables = rawTables
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        if (entry && typeof entry === 'object' && 'table_name' in entry) {
          const value = (entry as { table_name: unknown }).table_name;
          return typeof value === 'string' ? value : '';
        }
        return '';
      })
      .filter((name): name is string => name.length > 0);

    const databases = Array.isArray(envelope?.databases)
      ? envelope.databases.filter(
          (entry): entry is NotRegisteredTableDatabase =>
            !!entry && typeof entry === 'object',
        )
      : [];

    return { tables, databases };
  },
};